import React, { useMemo, useState } from "react";
import { Button, Modal, Row, Col, Upload, message, Divider, Input } from "antd";
import { DownloadOutlined, UploadOutlined, ArrowLeftOutlined, FilePdfOutlined, UserOutlined, DeleteOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { url as API_BASE_URL } from "components/common/api";
import { useFetchDesignerJobcardsQuery, useUpdateJobcardImagesMutation, useUpdateJobcardMutation, useUploadJobcardImagesMutation } from "features/store";
import { useDispatch } from "react-redux";
import { allApi } from "features/jobinv/AllApi";
import { useSelector } from "react-redux";
import { roleSelector } from "features/jobinv/JobCardSlice";
import { getImage, getImageUploader } from "../helper";
import { downloadS3File } from "../utils/downloadFile";
import { formatImageCreatedAt } from "../utils/timezoneUtils";

const SIDES = ["Front", "Right Middle", "Left Middle", "Back", "Velcro", "Visor"];

const TopBar = () => (
  <div style={{
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 10,
  }} />
);

// Hover styles for interactive image cards
const ImageCardStyles = () => (
  <style>
    {`
      .md-img-card { position: relative; border-radius: 18px; overflow: hidden; box-shadow: 0 6px 16px rgba(0,0,0,0.15); height: 180px; background: #f0f0f0; cursor: zoom-in; }
      .md-img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 220ms ease, filter 220ms ease; }
      .md-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.35)); opacity: 0; transition: opacity 220ms ease; }
      .md-img-card:hover .md-img { transform: scale(1.045); filter: saturate(1.02); }
      .md-img-card:hover .md-overlay { opacity: 1; }
      .md-download { transition: transform 220ms ease, background 220ms ease, color 220ms ease; }
      .md-img-card:hover .md-download { transform: translateX(-50%) translateY(-2px); background: #111; color: #fff; }
      .image-cards-container { 
        display: flex; 
        flex-wrap: wrap; 
        gap: 16px; 
        align-items: flex-start; 
      }
      @media (max-width: 768px) {
        .image-cards-container { flex-direction: column; }
        .image-cards-container > div { width: 100% !important; }
      }
    `}
  </style>
);

const DigitizerJobcardUpdate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { data } = useFetchDesignerJobcardsQuery();
  const [updateJobcardImages, updateInfo] = useUpdateJobcardImagesMutation();
  const [uploadJobcardImages, uploadInfo] = useUploadJobcardImagesMutation();
  const [updateJobcard] = useUpdateJobcardMutation();

  const initialJobcard = location.state?.jobcard;
  const fallbackJobcard = useMemo(() => (data?.results || []).find(j => String(j.id) === String(id)), [data, id]);
  const jobcard = fallbackJobcard || initialJobcard;
  const computedMode = useMemo(() => {
    if (!jobcard) return 'update';
    const imgs = jobcard.jobcard_images || [];
    const designerSides = new Set(
      imgs.filter(img => img && img.side && getImageUploader(img) === "Designer").map(img => img.side)
    );
    const digitizerSides = new Set(
      imgs.filter(img => img && img.side && img.is_degitizer_image).map(img => img.side)
    );
    const allRequiredDone = Array.from(designerSides).every(side => digitizerSides.has(side));
    return allRequiredDone ? 'update' : 'create';
  }, [jobcard]);
  const mode = location.state?.mode || computedMode; // 'create' or 'update'

  const [filesBySide, setFilesBySide] = useState({});
  const [titlesBySide, setTitlesBySide] = useState({});
  const [descriptionsBySide, setDescriptionsBySide] = useState({});
  const role = useSelector(roleSelector);
  const userLocal = JSON.parse(localStorage.getItem("user") || "null");
  const isDegitizer = role === "degitizer" || userLocal?.roll === "degitizer";

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewTitle, setPreviewTitle] = useState("");
  const dispatch = useDispatch();

  const withBase = (src) => (src?.startsWith("http") ? src : `${API_BASE_URL}${src?.startsWith("/") ? "" : "/"}${src || ""}`);

  const downloadImage = async (url, suggestedName) => {
    try {
      await downloadS3File(url, suggestedName);
    } catch (e) {
      message.error("Download failed");
    }
  };

  const getExtFromPath = (path) => {
    if (!path) return "";
    try {
      const clean = String(path).split("?")[0];
      const lastDot = clean.lastIndexOf(".");
      return lastDot > -1 ? clean.slice(lastDot) : "";
    } catch {
      return "";
    }
  };

  const handleFileChange = (side, file) => {
    // Just store the file, don't auto-upload
    if (!file) return false;
    setFilesBySide((prev) => ({ ...prev, [side]: file }));
    return false; // prevent auto upload
  };

  const handleRemoveFile = (side) => {
    setFilesBySide((prev) => {
      const next = { ...prev };
      delete next[side];
      return next;
    });
  };

  const doUpload = async () => {
    if (!jobcard) return;
    const selectedFiles = Object.entries(filesBySide).filter(([, f]) => !!f);
    if (selectedFiles.length === 0) {
      message.warning("Please select at least one side to upload");
      return;
    }

    // Allow upload ONLY for sides that came from designer (designer-provided images)
    const designerSides = new Set(
      (jobcard.jobcard_images || [])
        .filter((img) => img && img.side && getImageUploader(img) === "Designer")
        .map((img) => img.side)
    );
    const invalidSides = selectedFiles.map(([side]) => side).filter((side) => !designerSides.has(side));
    if (invalidSides.length) {
      message.error(`You can only upload for sides sent by designer. Invalid: ${invalidSides.join(", ")}`);
      return;
    }

    // Create a map of existing digitizer images by side
    const existingDigitizerImages = {};
    (jobcard.jobcard_images || [])
      .filter((img) => img && img.side && img.is_degitizer_image)
      .forEach((img) => {
        existingDigitizerImages[img.side] = img;
      });

    const username = userLocal?.username || userLocal?.email || "unknown";
    const allImages = jobcard.jobcard_images || [];
    const designerBySide = {};
    allImages.filter(img => img && img.side && !img.is_degitizer_image).forEach(img => { designerBySide[img.side] = img; });

    // Split selected files into update and create groups
    const filesToUpdate = [];
    const filesToCreate = [];

    for (const [side, f] of selectedFiles) {
      if (existingDigitizerImages[side] && existingDigitizerImages[side].id) {
        filesToUpdate.push([side, f]);
      } else {
        filesToCreate.push([side, f]);
      }
    }

    try {
      // Handle updates (PATCH) - requires existing image IDs
      if (filesToUpdate.length > 0) {
        const updateMetadata = [];
        const updateFiles = [];

        for (const [side, f] of filesToUpdate) {
          const originalName = f?.name || "";
          const dotIndex = originalName.lastIndexOf(".");
          const ext = dotIndex > -1 ? originalName.slice(dotIndex) : "";
          const safeSide = side.replace(/\s+/g, "_");
          const filename = `jobcard_${safeSide}${ext}`;
          const renamed = new File([f], filename, { type: f.type, lastModified: f.lastModified });

          const existingImage = existingDigitizerImages[side];
          const fallbackTitle = designerBySide[side]?.title;
          updateMetadata.push({
            id: existingImage.id,
            filename,
            side,
            created_by: username,
            title: (titlesBySide[side] ?? fallbackTitle) || null,
            description: descriptionsBySide[side] || null,
          });
          updateFiles.push(renamed);
        }

        await updateJobcardImages({
          metadata: updateMetadata,
          files: updateFiles,
        }).unwrap();
      }

      // Handle new uploads (POST) - no IDs needed
      if (filesToCreate.length > 0) {
        const createMetadata = [];
        const createFiles = [];

        for (const [side, f] of filesToCreate) {
          const originalName = f?.name || "";
          const dotIndex = originalName.lastIndexOf(".");
          const ext = dotIndex > -1 ? originalName.slice(dotIndex) : "";
          const safeSide = side.replace(/\s+/g, "_");
          const filename = `jobcard_${safeSide}${ext}`;
          const renamed = new File([f], filename, { type: f.type, lastModified: f.lastModified });

          const fallbackTitle = designerBySide[side]?.title;
          createMetadata.push({
            filename,
            side,
            title: (titlesBySide[side] ?? fallbackTitle) || null,
            description: descriptionsBySide[side] || null,
          });
          createFiles.push(renamed);
        }

        await uploadJobcardImages({
          job_card_id: jobcard.id,
          is_degitizer_image: true,
          metadata: createMetadata,
          files: createFiles,
        }).unwrap();
      }
      // After successful upload, set the corresponding side flags to true on the jobcard
      const sideKeyMap = {
        "Front": "is_front",
        "Right Middle": "is_right_middle",
        "Left Middle": "is_left_middle",
        "Back": "is_back",
        "Velcro": "is_velcro",
        "Visor": "is_visor",
      };
      const sideUpdates = {};
      for (const [side] of selectedFiles) {
        const key = sideKeyMap[side];
        if (key) sideUpdates[key] = true;
      }
      if (Object.keys(sideUpdates).length) {
        try {
          await updateJobcard({ id: jobcard.id, ...sideUpdates }).unwrap();
        } catch (e) {
          // If this fails, images are still uploaded; user can retry updating flags by reloading
        }
      }
      message.success("Images updated");
      // Clear the form
      setFilesBySide({});
      setTitlesBySide({});
      setDescriptionsBySide({});
      // Invalidate designer jobcards list so previous page refetches
      dispatch(allApi.util.invalidateTags(["DesignerJobcards"]));
      navigate(-1);
    } catch (e) {
      message.error("Update failed");
    }
  };

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>
      <ImageCardStyles />
      <TopBar />
      <div style={{ padding: 24 }}>

        {jobcard && (
          <>
            {/* Main Image Card */}
            {jobcard.main_image && (
              <>
                <Divider orientation="center">Main Image</Divider>
                <div style={{ maxWidth: 1280, margin: "0 auto" }}>
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      padding: 20,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      marginBottom: 16,
                      width: "fit-content",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 16,
                        paddingBottom: 12,
                        borderBottom: "2px solid #f0f0f0",
                      }}
                    >
                      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#333" }}>
                        Main Image
                      </h2>
                    </div>

                    <div
                      className="md-img-card"
                      style={{ position: "relative", width: 180, height: 180 }}
                    >
                      {(() => {
                        const rawSrc = jobcard.main_image;
                        const imageUrl = withBase(rawSrc);
                        const lower = String(rawSrc || "").toLowerCase();
                        const isPdf = lower.endsWith('.pdf');
                        const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
                        const ext = getExtFromPath(rawSrc) || "";

                        return (
                          <>
                            {imageUrl ? (
                              isPdf ? (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <FilePdfOutlined style={{ fontSize: 48, color: "#cf1322" }} />
                                </div>
                              ) : isImage ? (
                                <img src={imageUrl} alt="Main Image" className="md-img" />
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                                  <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                                </div>
                              )
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                                <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                              </div>
                            )}

                            <div className="md-overlay" />

                            <button
                              onClick={() => downloadImage(imageUrl, `${jobcard.serial_number || jobcard.id}_main_image${ext}`)}
                              style={{
                                position: "absolute",
                                bottom: 10,
                                left: "50%",
                                transform: "translateX(-50%)",
                                border: 0,
                                background: "#ffffff",
                                color: "#111",
                                cursor: "pointer",
                                fontSize: 16,
                                padding: "8px 12px",
                                borderRadius: 999,
                                fontWeight: 600,
                                zIndex: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              }}
                              className="md-download"
                            >
                              <DownloadOutlined />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}

            <Divider orientation="center">Master Images</Divider>
            <div style={{ maxWidth: 1280, margin: "0 auto" }}>
              {(() => {
                const allImages = jobcard.jobcard_images || [];
                const designerImages = allImages.filter((img) => !img.is_degitizer_image);
                const digitizerImages = allImages.filter((img) => img.is_degitizer_image);

                const renderImageCard = (img, index) => {
                  const rawSrc = getImage(img);
                  const imageUrl = withBase(rawSrc);
                  const lower = String(rawSrc || "").toLowerCase();
                  const isPdf = lower.endsWith('.pdf');
                  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
                  const ext = getExtFromPath(rawSrc) || "";

                  return (
                    <div
                      key={img.id}
                      className="md-img-card"
                      style={{ position: "relative" }}
                    >
                      {/* Side badge */}
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          background: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          fontSize: 12,
                          padding: "4px 10px",
                          borderRadius: 999,
                          zIndex: 2,
                          fontWeight: 600,
                        }}
                      >
                        {img.side || "Image"}
                      </div>
                      {img.created_at && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            fontSize: 9,
                            padding: "2px 6px",
                            borderRadius: 4,
                            zIndex: 2,
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatImageCreatedAt(img.created_at)}
                        </div>
                      )}

                      {imageUrl ? (
                        isPdf ? (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FilePdfOutlined style={{ fontSize: 48, color: "#cf1322" }} />
                          </div>
                        ) : isImage ? (
                          <img
                            src={imageUrl}
                            alt={img.side}
                            className="md-img"
                          />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                            <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                          </div>
                        )
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                          <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                        </div>
                      )}

                      {/* overlay for hover effect */}
                      <div className="md-overlay" />

                      {/* Download icon at bottom */}
                      <button
                        onClick={() => downloadImage(imageUrl, `${jobcard.serial_number || jobcard.id}_${img.side}${ext}`)}
                        style={{
                          position: "absolute",
                          bottom: 10,
                          left: "50%",
                          transform: "translateX(-50%)",
                          border: 0,
                          background: "#ffffff",
                          color: "#111",
                          cursor: "pointer",
                          fontSize: 16,
                          padding: "8px 12px",
                          borderRadius: 999,
                          fontWeight: 600,
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                        className="md-download"
                      >
                        <DownloadOutlined />
                      </button>
                    </div>
                  );
                };

                const renderCard = (title, images, icon) => {
                  if (!images.length) return null;

                  // Grid columns: for 1-2 images use 2 cols, for 3-5 use actual count, for 6+ use 6
                  const gridCols = images.length >= 6 ? 6 : images.length <= 2 ? 2 : images.length;

                  return (
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        marginBottom: 16,
                        // For 6+ images: full width, for fewer: auto-fit to content
                        width: images.length >= 6 ? "100%" : "fit-content",
                        flex: images.length >= 6 ? "1 1 100%" : "0 0 auto",
                      }}
                    >
                      {/* Card Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginBottom: 16,
                          paddingBottom: 12,
                          borderBottom: "2px solid #f0f0f0",
                        }}
                      >
                        {icon && (
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: title === "Designer" ? "#1890ff" : "#52c41a",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                            }}
                          >
                            {icon}
                          </div>
                        )}
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#333" }}>
                          {title}
                        </h2>
                      </div>

                      {/* Images Grid */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: images.length === 1
                            ? "180px"
                            : images.length === 2
                              ? "repeat(2, 180px)"
                              : images.length >= 6
                                ? "repeat(6, 1fr)"
                                : `repeat(${gridCols}, minmax(180px, 1fr))`,
                          gap: 16,
                          width: "fit-content",
                        }}
                      >
                        {images.map((img, index) => renderImageCard(img, index))}
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="image-cards-container">
                    {renderCard(
                      "Designer",
                      designerImages,
                      <UserOutlined style={{ fontSize: 16 }} />
                    )}
                    {renderCard(
                      "Digitizer",
                      digitizerImages,
                      <UserOutlined style={{ fontSize: 16 }} />
                    )}
                  </div>
                );
              })()}
            </div>

            <Divider orientation="center">Embroidery Files</Divider>
            <div style={{ maxWidth: 1280, margin: "0 auto" }}>
              <div style={{ background: "#fff", padding: 20, borderRadius: 12, border: "1px solid #eee", marginBottom: 20 }}>
                <Row gutter={[24, 24]}>
                  {(() => {
                    const sideMeta = [
                      { label: "Front", key: "is_front" },
                      { label: "Right Middle", key: "is_right_middle" },
                      { label: "Left Middle", key: "is_left_middle" },
                      { label: "Back", key: "is_back" },
                      { label: "Velcro", key: "is_velcro" },
                      { label: "Visor", key: "is_visor" },
                    ];
                    const designerSides = new Set(
                      (jobcard.jobcard_images || [])
                        .filter(img => img && img.side && getImageUploader(img) === "Designer")
                        .map(img => img.side)
                    );
                    const digitizerDone = new Set(
                      (jobcard.jobcard_images || [])
                        .filter(img => img && img.side && img.is_degitizer_image)
                        .map(img => img.side)
                    );
                    const embroidaryDone = new Set(
                      (jobcard.jobcard_images || [])
                        .filter(img => img && img.side && getImageUploader(img) === "Embroidary")
                        .map(img => img.side)
                    );
                    return sideMeta
                      .filter(({ label }) => designerSides.has(label) && !embroidaryDone.has(label))
                      .map(({ label, key }) => {
                        const alreadyDone = digitizerDone.has(label);
                        const selected = filesBySide[label];
                        const title = titlesBySide[label] || "";
                        const description = descriptionsBySide[label] || "";
                        return (
                          <Col span={8} key={key}>
                            <div style={{
                              border: alreadyDone ? "1px solid #eaeaea" : "1px dashed #bfbfbf",
                              borderRadius: 12,
                              background: "#fcfcfc",
                              padding: 16,
                              display: "flex",
                              flexDirection: "column",
                              gap: 12
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                              {alreadyDone && !selected && (
                                <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
                                  Already uploaded • choose file to replace
                                </div>
                              )}
                              <>
                                <Upload.Dragger
                                  accept=".emb"
                                  beforeUpload={(file) => {
                                    handleFileChange(label, file);
                                    return false;
                                  }}
                                  multiple={false}
                                  maxCount={1}
                                  showUploadList={false}
                                  style={{ width: "100%", background: "transparent", padding: 8, border: "none" }}
                                >
                                  <div>
                                    <UploadOutlined style={{ fontSize: 28, color: selected ? "#52c41a" : "#1677ff" }} />
                                    <div style={{ marginTop: 8, color: "#8c8c8c", fontSize: 12 }}>
                                      {selected ? selected.name : "Click or drag to upload"}
                                    </div>
                                  </div>
                                </Upload.Dragger>

                                {selected && (
                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "6px 10px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: 8,
                                      background: "#f5f5f5",
                                    }}
                                  >
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: 12,
                                        color: "#595959",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={selected.name}
                                    >
                                      {selected.name}
                                    </span>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemoveFile(label);
                                      }}
                                      style={{ color: "#cf1322" }}
                                    />
                                  </div>
                                )}

                                {/* Title Input */}
                                <Input
                                  placeholder="Title (optional)"
                                  value={title}
                                  onChange={(e) => setTitlesBySide((prev) => ({ ...prev, [label]: e.target.value }))}
                                  style={{ marginTop: 8 }}
                                />

                                {/* Description Input */}
                                <Input.TextArea
                                  placeholder="Description (optional)"
                                  value={description}
                                  onChange={(e) => setDescriptionsBySide((prev) => ({ ...prev, [label]: e.target.value }))}
                                  rows={2}
                                  style={{ marginTop: 8 }}
                                />
                              </>
                            </div>
                          </Col>
                        );
                      });
                  })()}
                </Row>

                {/* Single Upload Button */}
                <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={doUpload}
                    loading={uploadInfo.isLoading || updateInfo.isLoading}
                    disabled={Object.keys(filesBySide).length === 0}
                    style={{ minWidth: 200 }}
                  >
                    Upload Selected Images
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Image Preview */}
      <Modal
        title={previewTitle || "Preview"}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={900}
      >
        <Row gutter={[12, 12]}>
          {previewImages.map((img) => (
            <Col span={8} key={img.id}>
              <div style={{
                width: "100%",
                height: 180,
                border: "1px solid #eee",
                borderRadius: 6,
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}>
                {(() => {
                  const src = getImage(img);
                  const lower = String(src || "").toLowerCase();
                  const isPdf = lower.endsWith('.pdf');
                  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
                  if (isPdf) return <FilePdfOutlined style={{ fontSize: 48, color: "#cf1322" }} />;
                  if (isImage) return <img src={withBase(src)} alt={img.side} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />;
                  return <span role="img" aria-label="file" style={{ fontSize: 40, lineHeight: 1 }}>🗂️</span>;
                })()}
              </div>
              <div style={{ textAlign: "center", marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {img.side}
                {String(getImage(img)).toLowerCase().endsWith('.pdf') && (
                  <FilePdfOutlined style={{ color: "#cf1322" }} />
                )}
              </div>
            </Col>
          ))}
        </Row>
      </Modal>

      {/* Removed manual confirmation; role determines flag */}
    </div>
  );
};

export default DigitizerJobcardUpdate;

